/**
 * softphoneStore — ITEM 97 / §9.
 *
 * Makes the softphone reachable from anywhere in the tree.
 *
 * WHAT THE CODE SAID ABOUT ITSELF. `modules/callbacks/CallbacksWidget.tsx:11`:
 *
 *   "There is no softphone context/store in this codebase — `useSoftphone()` is
 *    instantiated once at the workspace root (it needs a SIP config) and the
 *    resulting SoftphoneObject is threaded down as a prop... To stay consistent
 *    with that pattern, the widget takes `dial` as a prop."
 *
 * The hook MUST stay at the root: it owns a JsSIP UA, media elements and device
 * enumeration, and a second instance would register a second SIP endpoint for
 * the same agent. That part of the design is correct and unchanged.
 *
 * The defect is what follows from it. Every component that wants to place a call
 * must sit on an unbroken prop chain from `AgentLayout`, and only three sites
 * do. Anything off that chain — click-to-call from a contact list, directory
 * transfer, internal calls — cannot dial at all. That is why the gap analysis
 * calls this "one fix unblocking click-to-call, directory transfer, and internal
 * calls".
 *
 * WHAT THIS HOLDS — AND WHAT IT DELIBERATELY DOES NOT
 * ---------------------------------------------------
 * It holds the ACTIONS: `dial`, `hangup`, `transfer`, `sendDtmf`. Stable
 * function references any component can call without a prop.
 *
 * It does NOT mirror `callState`, `registered` or `isMuted`. Those are React
 * state owned by the hook, and copying them here would create a second source of
 * truth that drifts — the exact defect items 84 (two SLA queries) and 107 (three
 * schema definitions) exist to clean up. Components needing live call state
 * subscribe to the hook via props, or to the existing `callStore` which is fed
 * from the server.
 *
 * WHY IT NEVER THROWS
 * -------------------
 * A component can render before the root hook has registered, or when the agent
 * has no SIP config at all. Throwing from a click handler would surface as a
 * blank crash screen instead of a dead button. But silently doing nothing is the
 * pathology this remediation exists to remove — so every action returns a result
 * saying whether it was delivered, and callers can disable UI on
 * `isAvailable()`.
 */
import { create } from 'zustand'

/** The subset of the softphone object distant components need. */
export interface SoftphoneActions {
  dial: (phoneE164: string) => void
  hangup?: () => void
  /** Absent on hosts that do not support it (the legacy workspace). */
  transfer?: (target: string) => void
  sendDtmf?: (digit: string) => void
}

export type SoftphoneCallResult =
  | { ok: true }
  | { ok: false; reason: string }

interface SoftphoneStore {
  /** Null until the root hook registers. */
  actions: SoftphoneActions | null
  /**
   * Called by the component owning `useSoftphone`. A later call REPLACES an
   * earlier one: on a re-mount (config change, reconnect) the first hook's
   * closures point at a UA that is being torn down.
   */
  register: (actions: SoftphoneActions) => void
  /** Called on unmount/logout, so no closure outlives its UA. */
  unregister: () => void
  isAvailable: () => boolean
  dial: (phoneE164: string) => SoftphoneCallResult
  hangup: () => SoftphoneCallResult
  transfer: (target: string) => SoftphoneCallResult
  sendDtmf: (digit: string) => SoftphoneCallResult
}

const NOT_REGISTERED = 'softphone is not registered — no SIP session is available'

export const useSoftphoneStore = create<SoftphoneStore>((set, get) => ({
  actions: null,

  register: (actions) => set({ actions }),
  unregister: () => set({ actions: null }),

  isAvailable: () => get().actions !== null,

  dial: (phoneE164) => {
    const a = get().actions
    if (!a) return { ok: false, reason: NOT_REGISTERED }
    a.dial(phoneE164)
    return { ok: true }
  },

  hangup: () => {
    const a = get().actions
    if (!a?.hangup) return { ok: false, reason: NOT_REGISTERED }
    a.hangup()
    return { ok: true }
  },

  transfer: (target) => {
    const a = get().actions
    // Distinguishes "no softphone" from "this host has no transfer": both are
    // a refusal, but only the second is a permanent capability gap.
    if (!a) return { ok: false, reason: NOT_REGISTERED }
    if (!a.transfer) return { ok: false, reason: 'this softphone host does not support transfer' }
    a.transfer(target)
    return { ok: true }
  },

  sendDtmf: (digit) => {
    const a = get().actions
    if (!a) return { ok: false, reason: NOT_REGISTERED }
    if (!a.sendDtmf) return { ok: false, reason: 'this softphone host does not support DTMF' }
    a.sendDtmf(digit)
    return { ok: true }
  },
}))
